import { Injectable, createComponent, ApplicationRef, EnvironmentInjector, inject, Type } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ReportRegistry, ReportRegistryKeys } from '../reports/report-registry';
import { lastValueFrom } from 'rxjs';
import { LoaderService } from './loader.service';
import html2pdf from 'html2pdf.js';
import { environment } from '../../environments/environment'

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  private appRef = inject(ApplicationRef);
  private injector = inject(EnvironmentInjector);

  constructor(private loader: LoaderService, private web: HttpClient) { }

  async generatePDF(reportType: ReportRegistryKeys, data: any) {
    this.loader.show();
    let rendered: { element: HTMLElement; destroy: () => void } | undefined;
    try {
    rendered = await this.renderComponent(reportType, data);
    const element = rendered.element;
    await document.fonts.ready;
    await Promise.all(Array.from(element.querySelectorAll('img')).map(img => img.decode()));



    const options = {
      filename: 'report.pdf',
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      html2canvas: {
        useCORS: true,
        scale: 2,
        logging: false
      }
    };
    const blob = await html2pdf().set(options).from(element).toPdf().output('blob');
    const base64 = await this.blobToBase64(blob);
    return base64;
    } finally {
      rendered?.destroy();
      this.loader.hide();
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // FIXED: this used to always instantiate PurchaseOrderConfirmComponent
  // regardless of reportType — now it actually looks up the component from
  // ReportRegistry, so DeliveryOrder/BillOfLading (and anything added
  // later) work the same way purchase-order confirmations already do.
  //
  // "as Type<any>" is needed because the three registered components have
  // completely different @Input() shapes (supplier/purchaseDate/etc. vs
  // just OrderID) — TypeScript can't unify those into one type when picking
  // a component class generically out of the registry, so this tells it not
  // to try. Purely a compile-time annotation; doesn't change runtime behavior.
  private async renderComponent(reportType: ReportRegistryKeys, data: any): Promise<{ element: HTMLElement; destroy: () => void }> {
    const ComponentClass = ReportRegistry[reportType] as Type<any>;

    const componentRef = createComponent(ComponentClass, {
      environmentInjector: this.injector
    });

    // Set data
    componentRef.instance.data = data;

    // Attach to app
    this.appRef.attachView(componentRef.hostView);
    const destroy = () => {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    };
    try {

    // Append to off-DOM container
    const container = document.getElementById('report-render-container');
    if (!container) throw new Error('Report render container is unavailable.');
    container.innerHTML = '';
    const domElem = componentRef.location.nativeElement;
    container!.appendChild(domElem);

    // Let Angular render it
    componentRef.changeDetectorRef.detectChanges();
    const ready = (componentRef.instance as any).reportReady;
    if (ready) await ready;
    else await this.delay(1000);
    componentRef.changeDetectorRef.detectChanges();
    return { element: domElem, destroy };
    } catch (error) {
      destroy();
      throw error;
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  public getReport(reportName: string, reportParams?: { value: any; name: any }[], format: 'pdf' | 'html' = 'html') {
    let params = new HttpParams().set('report', reportName).set('format', format);
    const token = localStorage.getItem('msal.idtoken') || localStorage.getItem('authToken');
    if (reportParams) {
      reportParams.forEach(param => {
        params = params.append(param.name, param.value);
      });
    }

    return this.web.get(environment.WS_URL + "/api/v1/reports/report", { params, responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }); // for PDFs
  }
}
